import { useCampaignData, useJobillaOrgId } from '@/hooks'
import { downloadBlob } from '@/lib'
import { CampaignCandidates, DEFAULT_PIPELINE_STAGES, LinkLocalProps, logger } from '@/ui'
import { formSubmissionExportCsv, useOrganisationPipelineConfigShow } from '@jobilla/nexus-sdk'
import { createFileRoute, Link, Outlet, useMatches, useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import { useCandidates } from './-useCandidates'
import { usePipelineStages } from './-usePipelineStages'

export const Route = createFileRoute(
	'/_auth/company/$companyId/$campaignId/candidates',
)({
	component: RouteComponent,
})

function RouteComponent() {
	const params = Route.useParams()
	const matches = useMatches()
	const jobillaOrgId = useJobillaOrgId()
	const navigate = useNavigate()

	// Check if a child route with candidateId is active
	const candidateMatch = matches.find((match) =>
		match.routeId === '/_auth/company/$companyId/$campaignId/candidates/$candidateId'
	)
	const selectedCandidateId = candidateMatch?.params?.candidateId ?? null

	const {
		campaign,
		isLoading,
		stages,
		candidates,
		selectedCandidate,
		handleCandidateMove,
		refetchCandidates,
		unreadEmailCount,
		unreadWhatsAppCount,
	} = useCandidates(
		params.campaignId,
		selectedCandidateId,
	)

	const {
		handleStageRename,
		handleStageAdd,
		handleStageDelete,
		handleStagesReorder,
		handleResetToDefaults,
		clearResetError,
		getCandidateCounts,
		isDeleting,
		isResetting,
		resetErrorMessage,
	} = usePipelineStages(params.campaignId)

	// Fetch organisation's default pipeline configuration
	const orgPipelineConfig = useOrganisationPipelineConfigShow(jobillaOrgId, {
		enabled: !!jobillaOrgId,
	})

	// Get effective default stages - fall back to DEFAULT_PIPELINE_STAGES if org hasn't configured custom ones
	const effectiveDefaultStages = useMemo(() => {
		const apiStages = orgPipelineConfig.data?.stages
		if (apiStages && apiStages.length > 0) {
			// Check if there are any non-locked (custom) stages
			const hasCustomStages = apiStages.some((s) => !s.locked)
			if (hasCustomStages) {
				return apiStages
			}
		}
		// Fall back to default stages if no custom config
		return [...DEFAULT_PIPELINE_STAGES]
	}, [orgPipelineConfig.data?.stages])

	// Handler for reset to defaults - just passes migrations to the hook
	const handleResetToDefaultsWithMigrations = useCallback((
		migrations: Record<string, string>,
		onSuccess?: () => void,
	) => {
		handleResetToDefaults(migrations, onSuccess)
	}, [handleResetToDefaults])

	// This is the parsed campaign type. We could parse the `campaign` above as a refactor.
	const { campaign: transformedCampaign } = useCampaignData({ campaignId: params.campaignId })

	const downloadCsv = async () => {
		if (!campaign.data?.form?.id) {
			return
		}

		try {
			const result = await formSubmissionExportCsv({ 'filter[form_id]': campaign.data.form.id })

			if (result.data) {
				// @ts-ignore
				const blob = new Blob([result.data], { type: 'text/csv' })
				downloadBlob(blob, `candidates-${campaign.data?.title.replace(' ', '-')}.csv`)
			}
		} catch (error) {
			logger.error(error, {
				category: 'candidates',
				tags: { feature: 'csv_export' },
				data: { campaignId: params.campaignId },
			})
		}
	}

	return (
		<CampaignCandidates
			loading={{
				candidates: isLoading,
				deleting: isDeleting,
				resetting: isResetting,
			}}
			errors={{
				resetToDefaults: resetErrorMessage ?? false,
			}}
			view={{
				campaign: transformedCampaign,
				candidates,
				selectedCandidate: selectedCandidate ?? null,
				stages,
				unreadEmailCount,
				unreadWhatsAppCount,
				candidateCounts: getCandidateCounts(),
			}}
			renderers={{
				candidateLink: (candidateId: string, props: LinkLocalProps) => (
					<Link
						to="/company/$companyId/$campaignId/candidates/$candidateId"
						params={{
							companyId: params.companyId,
							campaignId: params.campaignId,
							candidateId,
						}}
						onClick={() => {
							logger.track('Candidates: Clicked a Candidate', {
								data: {
									candidate_id: candidateId,
									campaign_id: params.campaignId,
									organisation_id: jobillaOrgId,
								},
							})
						}}
						{...props}
					/>
				),
			}}
			events={{
				onCsvExport: () => {
					downloadCsv()
				},
				onCandidateMove: handleCandidateMove,
				onCandidatesRefresh: refetchCandidates,
				onCandidateClick: (candidateId: string) => {
					logger.track('Candidates: Clicked a Candidate', {
						data: {
							candidate_id: candidateId,
							campaign_id: params.campaignId,
							organisation_id: jobillaOrgId,
						},
					})
					navigate({
						to: '/company/$companyId/$campaignId/candidates/$candidateId',
						params: {
							companyId: params.companyId,
							campaignId: params.campaignId,
							candidateId,
						},
					})
				},
				onSlideOverClose: () => {
					navigate({
						to: '/company/$companyId/$campaignId/candidates',
						params: {
							companyId: params.companyId,
							campaignId: params.campaignId,
						},
					})
				},
				onStageRename: handleStageRename,
				onStageAdd: handleStageAdd,
				onStageDelete: handleStageDelete,
				onStagesReorder: handleStagesReorder,
				onResetToDefaults: handleResetToDefaultsWithMigrations,
				onResetModalClose: clearResetError,
			}}
			organisationDefaults={{
				stages: effectiveDefaultStages,
				isLoading: orgPipelineConfig.isLoading,
			}}
		>
			<Outlet />
		</CampaignCandidates>
	)
}
